import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { BagStrip } from './components/BagStrip';
import { BagManageModal } from './components/BagManageModal';
import { BagDeleteConfirm } from './components/BagDeleteConfirm';
import { ProductTableToolbar } from './components/ProductTableToolbar';
import { AvisoEdicionVersion } from '../CentroCotizaciones/components/AvisosVersion';
import { ReprecioResumenModal } from '../CentroCotizaciones/components/ReprecioResumenModal';
import { ReprecioResumen } from '../CentroCotizaciones/types';
import { ProductRowCard } from './components/ProductRowCard';
import { WorkflowCard } from './components/WorkflowCard';
import { QuotationSummaryCard } from './components/QuotationSummaryCard';
import { SeleccionarProductoModal } from './components/SeleccionarProductoModal';
import SitioEntregaBlock from './components/SitioEntregaBlock';
import ValidezBlock from './components/ValidezBlock';
import { useDetalleCotizacion } from './useDetalleCotizacion';
import { cotizacionDetalleService, ENTREGA_VACIA } from './service';
import { ProductoDetalleModal } from '../../../catalogo/components/ProductoDetalleModal';
import { ProductoCatalogoPublico } from '../../../catalogo/types';
import { BolsasService } from '../../services/bolsasService';
import { resolverDestinoDetalle } from '../../services/resolverDestinoCrear';
import { NuevaBolsaModal } from '../../components/NuevaBolsaModal';
import { CreateBolsaDTO, Bolsa as BolsaResumen, LineaDeBolsa, DiscardBolsasResult } from '../../types';
import { LineaCotizacion } from './types';
import { calcularPrecioUnitario, TRANSPORTE_DEFAULT } from './domain/pricing';
import { aLineaResumen } from './utils/lineaResumen';
import { EstadoBolsa, ESTADOS_LABEL } from '../CentroCotizaciones/state/bolsaMachine';
import { BlockingOverlay } from '../../../../shared/components/BlockingOverlay';

// SPEC-115: acción diferida por el guard de cambios sin guardar — cambiar de
// bolsa (selector del header) o abrir el modal de nueva bolsa.
type PendingAction =
  | { type: 'cambiarBolsa'; bolsaId: string }
  | { type: 'nuevaBolsa' };

// fr proporcional = preserva proporciones originales + llena todo el espacio disponible
// SPEC-089: sin columna Total; "Valor Marcación" entre Marcación y Cantidad;
// "Precio de venta" pasa a ser la última columna de datos antes de Acciones
// SPEC-131: columna "Transporte" de vuelta, entre "Precio Unitario" y "Utilidad %" — el
// transporte volvió a ser un dato por línea (revierte el retiro de SPEC-119).
export const COLUMN_DEFS = [
  { label: 'Producto',         minWidth: '270px', fr: 18.5 },
  { label: 'Stock',            minWidth: '96px',  fr: 6.8  },
  { label: 'Color',            minWidth: '143px', fr: 9.68 },
  // SPEC-148 (RN-001): Cantidad se ubica inmediatamente a la izquierda de Marcación.
  { label: 'Cantidad',         minWidth: '142px', fr: 8.5  },
  { label: 'Marcación',        minWidth: '171px', fr: 11.55 },
  { label: 'Valor Marcación',  minWidth: '140px', fr: 9.5  },
  { label: 'Precio Unitario',  minWidth: '142px', fr: 10   },
  { label: 'Transporte',       minWidth: '124px', fr: 8.5  },
  { label: 'Utilidad %',       minWidth: '88px',  fr: 6.06 },
  { label: 'Precio de venta',  minWidth: '148px', fr: 10.2 },
  { label: '',                 minWidth: '96px',  fr: 4.5  },
] as const;

// SPEC-208 (RN-010) — columna "Opción" junto a Color, solo si alguna línea tiene más de una opción
// de precio; sin ella la tabla queda exactamente como antes.
export const COLUMNA_OPCION = { label: 'Opción', minWidth: '200px', fr: 11 } as const;

type ColumnaDetalle = (typeof COLUMN_DEFS)[number] | typeof COLUMNA_OPCION;

export function columnasDelDetalle(conOpcion: boolean): ColumnaDetalle[] {
  if (!conOpcion) return [...COLUMN_DEFS];
  const color = COLUMN_DEFS.findIndex(col => col.label === 'Color');
  return [...COLUMN_DEFS.slice(0, color + 1), COLUMNA_OPCION, ...COLUMN_DEFS.slice(color + 1)];
}

function plantillaDeColumnas(columnas: ColumnaDetalle[]): string {
  return columnas.map(col => `minmax(${col.minWidth}, ${col.fr}fr)`).join(' ');
}

// SPEC-172 (RN-001): tamaño de tanda de render. Solo se montan las primeras `TANDA` filas del
// listado filtrado; las demás bajo demanda. Client-side, sin dependencias (ADR-001).
const TANDA = 20;

// SPEC-150 (RN-003): precio congelado de la línea (precio de venta negociado si existe > 0; en su
// defecto, base con marcación y transporte/cantidad — misma fórmula que la fila).
// SPEC-160 (RN-002/RN-004): el modal de detalle YA NO usa este valor — dejó de mostrar el precio
// congelado de la línea y pasó a mostrar el precio del producto como tal (catálogo). Se conserva
// la función (lógica de duplicado/persistencia de SPEC-150 intacta) aunque buildProductoModal ya
// no la invoque.
export function precioCongeladoLinea(linea: LineaCotizacion): number {
  if (linea.precioVenta !== null && linea.precioVenta > 0) {
    return linea.precioVenta;
  }
  const precioBase = (linea.precioConDescuento ?? linea.precioUnitario) + (linea.precioMarcacion ?? 0);
  const transporte = linea.transporte ?? TRANSPORTE_DEFAULT;
  return calcularPrecioUnitario(precioBase, transporte, linea.cantidad);
}

export function buildProductoModal(linea: LineaCotizacion): ProductoCatalogoPublico {
  return {
    id: linea.canonicoId ?? null,
    canonicoId: linea.canonicoId ?? null,
    nombre: linea.nombre,
    descripcion: linea.descripcion,
    categoria: null,
    subcategoria: null,
    tags: [],
    // SPEC-160 (RN-002/RN-004): el modal muestra el precio del producto como tal (precio de
    // catálogo), no el precio congelado de la línea. Para productos con canónico el modal antepone
    // detalle?.precioDesde (catálogo); esta semilla/fallback es el precio base del producto de la
    // línea, SIN transporte, marcación ni venta negociada. Supersede parcial de SPEC-150 (visual).
    precioDesde: linea.precioConDescuento ?? linea.precioUnitario,
    precioHasta: null,
    stockTotal: Math.max(0, linea.coloresVariantes.reduce((sum, c) => sum + c.stock, 0)),
    coloresDisponibles: linea.coloresVariantes.map(c => ({
      nombre: c.color,
      codigoColor: c.codigo_color ?? null,
      stock: c.stock,
    })),
    imagenPrincipal: linea.imagenUrl,
    imagenes: linea.imagenUrl ? [{ url: linea.imagenUrl, orden: 0, color: null }] : [],
    proveedorProductoId: null,
    categoriaProveedor: linea.categoriaNombre ?? null,
    lastSynced: linea.lastSynced ?? null,
    sku: linea.sku ?? null,
    proveedorNombre: linea.proveedor ?? null,
  };
}

export function DetalleCotizacion() {
  const { bolsaId } = useParams<{ bolsaId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [descuento, setDescuento] = useState<number>(0);
  const [modalLineaId, setModalLineaId] = useState<string | null>(null);
  const [duplicandoId, setDuplicandoId] = useState<string | null>(null);
  const [modalAgregarOpen, setModalAgregarOpen] = useState(false);
  const [bolsasAbiertas, setBolsasAbiertas] = useState<BolsaResumen[]>([]);
  const [bolsasError, setBolsasError] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [nuevaBolsaModalOpen, setNuevaBolsaModalOpen] = useState(false);
  // SPEC-119 (R-10, R-11, UC-004..UC-007): estado de los 3 puntos de entrada de borrado.
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [deleteTargetIds, setDeleteTargetIds] = useState<string[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // SPEC-206 (CA-029) — al llegar desde "Duplicar": resumen del re-precio de la copia.
  const [resumenDuplicado, setResumenDuplicado] = useState<{ titulo: string; reprecio: ReprecioResumen } | null>(null);
  // SPEC-119 (RN-004, CA-012, CA-021): estado vacío de pantalla tras borrar la última bolsa.
  // SPEC-205 (RN-009, reemplaza SPEC-141 RN-006): se activa cuando NO queda ninguna cotización en borrador.
  const [sinBolsasAbiertas, setSinBolsasAbiertas] = useState(false);
  // SPEC-119 (CA-020): término de búsqueda del toolbar, filtra las líneas visibles.
  const [busqueda, setBusqueda] = useState('');
  // SPEC-172 (RN-001): tope de render incremental. Paginación SOLO de vista; no toca lineasState
  // ni los agregados (RN-002). Se reinicia al cambiar la búsqueda o recargar la bolsa (RN-004).
  const [tope, setTope] = useState(TANDA);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    bolsa,
    tiposMarcacion,
    lineasState,
    loading,
    error,
    guardando,
    guardado,
    hayCambiosSinGuardar,
    subtotal,
    cambiarColor,
    cambiarOpcion,
    cambiarMarcacion,
    confirmarMarcacion,
    cambiarCantidad,
    cambiarTransporte,
    cambiarUtilidad,
    aplicarUtilidadMasiva,
    aplicarCantidadMasiva,
    cambiarPrecioVenta,
    marcarCambioPendiente,
    getVarianteActual,
    guardarCotizacion,
    crearPdf,
    duplicarLinea,
    eliminarLinea,
    agregarProducto,
    // SPEC-167 — validez de construcción.
    validez,
    validezSugerida,
    validezError,
    validezGuardando,
    actualizarValidez,
  } = useDetalleCotizacion(bolsaId);

  // SPEC-208 (RN-010) — la columna "Opción" existe solo si alguna línea tiene más de una opción.
  const conOpcion = !!bolsa?.lineas.some(l => (l.opcionesPrecio?.length ?? 0) > 1);
  const columnas = useMemo(() => columnasDelDetalle(conOpcion), [conOpcion]);
  const gridTemplateColumns = useMemo(() => plantillaDeColumnas(columnas), [columnas]);

  // SPEC-119 (R-02): lista de bolsas para la franja de chips, en paralelo a la carga
  // de la bolsa activa. Se refresca también tras cada borrado (individual o en lote).
  // SPEC-205 (RN-008, reemplaza SPEC-141 RN-003/RN-007): la franja lista solo las cotizaciones
  // en BORRADOR, filtradas en el servidor. (La variable conserva el nombre `bolsasAbiertas` para
  // acotar el diff; su semántica es "cotizaciones en borrador".)
  const cargarBolsasAbiertas = useCallback(() => {
    return BolsasService.listarBolsas({ estado: EstadoBolsa.BORRADOR })
      .then(lista => {
        setBolsasAbiertas(lista);
        setBolsasError(false);
      })
      .catch(() => {
        setBolsasError(true);
      });
  }, []);

  useEffect(() => {
    cargarBolsasAbiertas().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolsaId]);

  // SPEC-122 (RN-001, RN-002): sincroniza en memoria la entrada de bolsasAbiertas de la
  // bolsa activa cada vez que sus líneas cambian (agregar/eliminar producto), sin refetch
  // a BolsasService.listarBolsas(). `bolsa.lineas` solo cambia de referencia al cargar,
  // agregar o eliminar una línea (ver useDetalleCotizacion.ts) — cambios de color/cantidad/
  // marcación viven en lineasState y no disparan este efecto.
  useEffect(() => {
    if (!bolsa) return;
    const bolsaIdActiva = bolsa.id;
    const lineas = bolsa.lineas;
    setBolsasAbiertas(prev => {
      const idx = prev.findIndex(b => b.id === bolsaIdActiva);
      if (idx === -1) return prev;
      const lineasResumen: LineaDeBolsa[] = lineas.map(aLineaResumen);
      const next = [...prev];
      next[idx] = { ...prev[idx], lineas: lineasResumen, lineas_count: lineas.length };
      return next;
    });
    // Depende solo de `bolsa.lineas` (por referencia) — este array solo cambia al cargar,
    // agregar o eliminar una línea; cambios de freight/color/cantidad no lo reasignan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolsa?.lineas]);

  // SPEC-133 (CA-002, RN-002): tras crearPdf() convertir una bolsa ACTIVA, sincroniza estado
  // y codigo_cotizacion en la franja para que el ícono de preview aparezca sin refetch.
  useEffect(() => {
    if (!bolsa) return;
    const bolsaIdActiva = bolsa.id;
    const estadoCodigo = bolsa.estadoCodigo;
    const codigoCotizacion = bolsa.codigoCotizacion;
    setBolsasAbiertas(prev => {
      const idx = prev.findIndex(b => b.id === bolsaIdActiva);
      if (idx === -1) return prev;
      if (prev[idx].estado_codigo === estadoCodigo && prev[idx].codigo_cotizacion === codigoCotizacion) {
        return prev;
      }
      const next = [...prev];
      next[idx] = { ...prev[idx], estado_codigo: estadoCodigo, codigo_cotizacion: codigoCotizacion };
      return next;
    });
    // Depende solo de los 3 campos primitivos que sincroniza — evita reejecutar en cada
    // cambio de referencia de `bolsa` (p.ej. edición de líneas, ya cubierta por el efecto anterior).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolsa?.id, bolsa?.estadoCodigo, bolsa?.codigoCotizacion]);

  // SPEC-205 (RN-008): la bolsa que se está viendo siempre está en la franja. Si no viene en la
  // lista de BORRADOR (ACTIVA antes de su primer Guardar, ENVIADA abierta desde el Centro…), su
  // resumen se antepone. Se deriva de `bolsa` en cada render, así refleja sin refetch las líneas
  // (SPEC-122) y la conversión (SPEC-133 / SPEC-182 RN-007).
  // SPEC-119 (RN-002/CA-007): si falla la carga o aún no llega, la franja degrada a solo esa bolsa.
  const resumenBolsaActual: BolsaResumen | null = bolsa ? {
    id: bolsa.id,
    nombre: bolsa.nombre,
    estado: bolsa.estadoCodigo,
    estado_codigo: bolsa.estadoCodigo,
    total: bolsa.total,
    lineas_count: bolsa.lineas_count,
    codigo_cotizacion: bolsa.codigoCotizacion,
    lineas: bolsa.lineas.map(aLineaResumen),
  } : null;
  const listaFranja = bolsasError ? [] : bolsasAbiertas;
  const opcionesBolsa: BolsaResumen[] =
    !resumenBolsaActual || listaFranja.some(b => b.id === resumenBolsaActual.id)
      ? listaFranja
      : [resumenBolsaActual, ...listaFranja];

  const lineaModal = bolsa?.lineas.find(l => l.id === modalLineaId) ?? null;
  const productoModal: ProductoCatalogoPublico | null =
    lineaModal ? buildProductoModal(lineaModal) : null;

  const handleProductoClick = useCallback((linea: LineaCotizacion) => {
    setModalLineaId(linea.id);
  }, []);

  // SPEC-172 (RN-003): `duplicarLinea` del hook recrea su referencia al editar (deps incluyen
  // bolsa/hayCambiosSinGuardar/guardarCotizacion). Se lee vía ref para que `handleDuplicar` sea
  // referencialmente ESTABLE (deps []) y no rompa el React.memo de todas las ProductRowCard.
  const duplicarLineaRef = useRef(duplicarLinea);
  useEffect(() => { duplicarLineaRef.current = duplicarLinea; }, [duplicarLinea]);
  const handleDuplicar = useCallback(async (lineaId: string) => {
    setDuplicandoId(lineaId);
    await duplicarLineaRef.current(lineaId);
    setDuplicandoId(null);
  }, []);

  const irACatalogo = () => navigate('/catalogo');
  const irACentroCotizaciones = () => navigate('/cotizaciones');

  // TASK-005: orquesta navegación entre bolsas, gateando por hayCambiosSinGuardar (RN-003)
  const handleSeleccionarBolsa = useCallback((nuevoBolsaId: string) => {
    if (!bolsa || nuevoBolsaId === bolsa.id) return;
    if (hayCambiosSinGuardar) {
      setPendingAction({ type: 'cambiarBolsa', bolsaId: nuevoBolsaId });
      return;
    }
    navigate(`/cotizaciones/detalle/${nuevoBolsaId}`);
  }, [bolsa, hayCambiosSinGuardar, navigate]);

  // SPEC-115 (TASK-002/RN-004): mismo guard de cambios sin guardar, ahora también
  // para abrir NuevaBolsaModal desde este punto de entrada.
  const handleAbrirNuevaBolsa = useCallback(() => {
    if (hayCambiosSinGuardar) {
      setPendingAction({ type: 'nuevaBolsa' });
      return;
    }
    setNuevaBolsaModalOpen(true);
  }, [hayCambiosSinGuardar]);

  const handleConfirmarPendingAction = useCallback(() => {
    if (pendingAction?.type === 'cambiarBolsa') {
      navigate(`/cotizaciones/detalle/${pendingAction.bolsaId}`);
    } else if (pendingAction?.type === 'nuevaBolsa') {
      setNuevaBolsaModalOpen(true);
    }
    setPendingAction(null);
  }, [pendingAction, navigate]);

  const handleCancelarPendingAction = useCallback(() => {
    setPendingAction(null);
  }, []);

  // SPEC-115 (TASK-003/TASK-004): crea la bolsa vía el mismo servicio que useBolsas.crearBolsa
  // y navega a su detalle señalando auto-apertura del selector de productos (RN-002/RN-003).
  // Si BolsasService.crearBolsa rechaza, NuevaBolsaModal captura el error, muestra el mensaje
  // y mantiene el modal abierto con los datos ingresados (CA-003) — no se navega.
  // SPEC-158 (RN-005): al crear desde el estado vacío se restablece `sinBolsasAbiertas` antes de
  // navegar, para que ninguna re-renderización residual muestre el vacío sobre una ruta con
  // :bolsaId. Si `crearBolsa` rechaza, la excepción se propaga a NuevaBolsaModal (CA-004) y ni el
  // reset ni la navegación se ejecutan. Complementa al guard condicionado a `!bolsaId` (RN-001).
  const handleCrearBolsa = useCallback(async (dto: CreateBolsaDTO) => {
    const nuevaBolsa = await BolsasService.crearBolsa(dto);
    setSinBolsasAbiertas(false);
    navigate(`/cotizaciones/detalle/${nuevaBolsa.id}`, { state: { autoOpenAgregarProducto: true } });
  }, [navigate]);

  // SPEC-115 (TASK-005): al montar la bolsa nueva con la señal de navegación, auto-abre
  // SeleccionarProductoModal. Se limpia el state con replace para que no se reabra en
  // refrescos o al volver a esta misma URL (se consume una sola vez).
  // SPEC-124 (RN-002, CA-002): mismo patrón para autoOpenNuevaBolsaModal — al aterrizar
  // desde "Crear" del sidebar en una bolsa abierta existente, reutiliza handleAbrirNuevaBolsa
  // (mismo guard/mecanismo que el botón "Nueva bolsa" ya certificado en SPEC-115).
  useEffect(() => {
    const state = location.state as
      | {
          autoOpenAgregarProducto?: boolean;
          autoOpenNuevaBolsaModal?: boolean;
          duplicado?: { titulo: string; reprecio: ReprecioResumen | null };
        }
      | null;
    if (state?.duplicado) {
      setToast(state.duplicado.titulo);
      if (state.duplicado.reprecio) setResumenDuplicado({ titulo: state.duplicado.titulo, reprecio: state.duplicado.reprecio });
      navigate(location.pathname, { replace: true, state: {} });
    } else if (state?.autoOpenAgregarProducto) {
      setModalAgregarOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (state?.autoOpenNuevaBolsaModal) {
      handleAbrirNuevaBolsa();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolsaId]);

  // Aterrizaje SIN :bolsaId (ruta /cotizaciones/detalle) — llega aquí "Bolsas"/"Crear"
  // cuando el resolver no tenía una bolsa concreta, o por navegación directa a la ruta.
  // Se resuelve con el MISMO resolver único: si hay cotizaciones en borrador (SPEC-205 RN-009),
  // se redirige al detalle de la más reciente conservando el state (para que "Crear" abra el pop-up);
  // si no hay ninguna, se muestra el estado vacío del Detalle. Nunca se sale del Detalle.
  useEffect(() => {
    if (bolsaId) return;
    let cancelled = false;
    resolverDestinoDetalle({ abrirCrear: false }).then((destino) => {
      if (cancelled) return;
      const match = destino.path.match(/\/cotizaciones\/detalle\/(.+)$/);
      if (match) {
        // Preserva el location.state (p. ej. autoOpenNuevaBolsaModal de "Crear").
        navigate(`/cotizaciones/detalle/${match[1]}`, { replace: true, state: location.state });
      } else {
        setSinBolsasAbiertas(true);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolsaId]);

  // SPEC-119 (R-05, R-10, RN-007): papelera de chip — abre confirmación sin activar la bolsa.
  const handleAbrirBorrarChip = useCallback((idBolsa: string) => {
    setDeleteTargetIds([idBolsa]);
  }, []);

  // SPEC-133 (CA-006, UC-003): ícono de preview del chip — consulta el PDF vigente y lo abre
  // en una pestaña nueva vía Blob/URL.createObjectURL (más eficiente que un data: URI para
  // binarios grandes).
  const handlePreviewPdf = useCallback(async (idBolsa: string) => {
    try {
      const { pdf_base64 } = await cotizacionDetalleService.obtenerPdf(idBolsa);
      const byteChars = atob(pdf_base64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Error al abrir el PDF');
    }
  }, []);

  // SPEC-119 (R-06, R-10): botón del encabezado de contenido — confirmación para la activa.
  const handleAbrirBorrarActiva = useCallback(() => {
    if (!bolsa) return;
    setDeleteTargetIds([bolsa.id]);
  }, [bolsa]);

  // SPEC-119 (R-11): desde "Gestionar bolsas" — selección múltiple o papelera de una fila.
  const handleSolicitarBorradoDesdeGestion = useCallback((ids: string[]) => {
    setManageModalOpen(false);
    setDeleteTargetIds(ids);
  }, []);

  // SPEC-119 (RN-002, RN-004, UC-006): único punto que reacciona a un discard confirmado.
  const handleDiscardConfirmado = useCallback(async (result: DiscardBolsasResult) => {
    setDeleteTargetIds(null);
    if (result.discarded.length > 0) {
      setToast(
        result.discarded.length === 1
          ? `Bolsa borrada`
          : `${result.discarded.length} bolsas borradas`,
      );
    }
    if (result.rejected.length > 0) {
      setToast(`${result.rejected.length} bolsa(s) no se pudieron borrar porque cambiaron de estado`);
    }

    // SPEC-205 (RN-009): la reubicación usa el mismo criterio de la franja (solo BORRADOR).
    const borradores = await BolsasService.listarBolsas({ estado: EstadoBolsa.BORRADOR });
    setBolsasAbiertas(borradores);

    // RN-004 (SPEC-119) / RN-009 (SPEC-205): si la bolsa activa fue descartada, activar la
    // primera cotización en borrador restante o, si no queda ninguna, el estado vacío. El vacío
    // solo se muestra sin :bolsaId (SPEC-158 RN-001, SPEC-205 RN-010), por eso se navega a la
    // ruta sin id en vez de quedarse en la de la bolsa descartada.
    if (bolsa && result.discarded.includes(bolsa.id)) {
      if (borradores.length > 0) {
        navigate(`/cotizaciones/detalle/${borradores[0].id}`);
      } else {
        setSinBolsasAbiertas(true);
        navigate('/cotizaciones/detalle');
      }
    }
  }, [bolsa, navigate]);

  // SPEC-172 (RN-001): monta la siguiente tanda. Usado por el botón «cargar más» y el centinela.
  const cargarMas = useCallback(() => setTope((t) => t + TANDA), []);

  // SPEC-172 (RN-004): reinicia el tope al cambiar la búsqueda o la bolsa, para mostrar desde la
  // primera coincidencia/línea. (Debe vivir con el resto de hooks, antes de los early returns.)
  useEffect(() => { setTope(TANDA); }, [busqueda, bolsaId]);

  // SPEC-172 (RN-001, CA-003): centinela de scroll. Al entrar en viewport monta la siguiente tanda.
  // Se re-crea al cambiar `tope` (para re-observar el nuevo centinela) y al filtrar/cambiar bolsa.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) cargarMas();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [busqueda, bolsaId, tope, cargarMas]);

  // SPEC-158 (RN-001): el estado vacío del Detalle solo es válido cuando la ruta NO apunta a una
  // bolsa concreta. Un `:bolsaId` presente siempre gana sobre `sinBolsasAbiertas` (residual tras
  // crear), de modo que crear una bolsa aterriza en su detalle en vez de quedar en el estado vacío.
  if (sinBolsasAbiertas && !bolsaId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 600, color: '#2E2430' }}>
          🛒 No hay cotizaciones en borrador
        </Typography>
        <Typography sx={{ fontSize: 16, color: '#A79AA7' }}>
          Crea una nueva desde el catálogo para empezar a mercar.
        </Typography>
        <Button
          data-testid="btn-nueva-bolsa-estado-vacio"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNuevaBolsaModalOpen(true)}
          sx={{ background: '#93408F', textTransform: 'none', borderRadius: '999px', '&:hover': { background: '#6E2F6B' } }}
        >
          Nueva bolsa
        </Button>
        <NuevaBolsaModal
          open={nuevaBolsaModalOpen}
          onClose={() => setNuevaBolsaModalOpen(false)}
          onCreateBolsa={handleCrearBolsa}
        />
      </Box>
    );
  }

  // Aterrizaje sin :bolsaId: mientras el efecto de arriba resuelve (redirigir a la
  // bolsa más reciente o mostrar el estado vacío), se muestra el spinner en vez del
  // mensaje "Bolsa no encontrada".
  if (!bolsaId && !sinBolsasAbiertas) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#A3489D' }} />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#A3489D' }} />
      </Box>
    );
  }

  if (!bolsa) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography data-testid="error-bolsa-no-encontrada" sx={{ color: '#E03030', fontSize: 20, fontWeight: 600 }}>
          {error ?? 'Bolsa no encontrada'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={irACatalogo}
          sx={{ mt: 2, color: '#A3489D', borderColor: '#A3489D', borderRadius: '8px' }}
        >
          Volver a bolsas
        </Button>
      </Box>
    );
  }

  // SPEC-117 (RN-001, RN-002) — alineado con ESTADOS_EDITABLES del backend (EstadoBolsa.ts):
  // ACTIVA (estado con el que nace toda bolsa nueva) y BORRADOR son editables; cualquier
  // otro estado bloquea escritura en esta página (guardar/agregar/duplicar/eliminar/editar línea).
  const ESTADOS_EDITABLES_DETALLE = [EstadoBolsa.ACTIVA, EstadoBolsa.BORRADOR];
  const soloLectura = !ESTADOS_EDITABLES_DETALLE.includes(bolsa.estadoCodigo);

  // SPEC-119 (CA-020): filtra las líneas visibles por nombre de producto según el buscador.
  const lineasFiltradas = busqueda.trim()
    ? bolsa.lineas.filter((l) => l.nombre.toLowerCase().includes(busqueda.trim().toLowerCase()))
    : bolsa.lineas;

  // SPEC-172 (RN-001): recorte de RENDER sobre el filtrado. NO altera lineasState ni los agregados
  // (RN-002): unidadesTotales/referenciasDistintas/subtotal/guardar siguen sobre bolsa.lineas.
  const lineasVisibles = lineasFiltradas.slice(0, tope);
  const hayMasLineas = lineasVisibles.length < lineasFiltradas.length;

  // SPEC-136 (RN-002, RN-005): stats/estados que antes vivían en BagContentHeader, ahora
  // se pasan a QuotationSummaryCard (pie de tabla) y BagStrip (franja de bolsas).
  const unidadesTotales = bolsa.lineas.reduce((acc, l) => acc + (lineasState[l.id]?.cantidad ?? l.cantidad), 0);
  // SPEC-168 (RN-001, RN-008): "referencias" del pie = productos distintos (por productoId) de la
  // bolsa activa, no el total de líneas. La bolsa activa siempre trae sus líneas completas → exacto.
  const referenciasDistintas = new Set(bolsa.lineas.map((l) => l.productoId)).size;
  const crearPdfDisabled = soloLectura || bolsa.lineas.length === 0;

  return (
    <Box sx={{ background: '#F7F7F8', minHeight: '100vh', px: 3, pt: 2, pb: 3 }}>
      <BlockingOverlay open={guardando} />
      {/* Page header */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs sx={{ fontSize: 15, color: '#8A8F99', mb: 0.5 }}>
          <Link underline="hover" color="inherit" onClick={irACatalogo} sx={{ fontSize: 15, cursor: 'pointer' }}>
            Catálogo
          </Link>
          <Link
            underline="hover"
            onClick={irACatalogo}
            sx={{ fontSize: 15, color: '#A3489D', fontWeight: 600, cursor: 'pointer' }}
          >
            Bolsa
          </Link>
          <Typography sx={{ fontSize: 15, color: '#A3489D', fontWeight: 600 }}>Cotización</Typography>
        </Breadcrumbs>

        {/* CA-019/020: pasos horizontales en la barra de título */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography data-testid="page-title" variant="h5" sx={{ fontWeight: 800, color: '#231F2B', fontSize: 27 }}>
            Detalle de cotización
          </Typography>
          {/* SPEC-132 (CA-001 a CA-004): tag de estado real de la bolsa, siempre visible */}
          <Box
            data-testid="tag-estado-bolsa"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#E4F8EB',
              color: '#20A75A',
              border: '1px solid #B7EDCB',
              borderRadius: '999px',
              px: '12px',
              py: '4px',
              fontSize: 15,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            {ESTADOS_LABEL[bolsa.estadoCodigo]}
          </Box>
          {soloLectura && (
            <Box
              data-testid="indicador-solo-lectura"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                background: '#F1E8F8',
                color: '#7C3E8E',
                border: '1px solid #D4B8E8',
                borderRadius: '999px',
                px: '12px',
                py: '4px',
                fontSize: 15,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Solo lectura
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <WorkflowCard horizontal onBolsaClick={irACatalogo} />
          <Button
            data-testid="btn-ir-centro-cotizaciones"
            variant="outlined"
            startIcon={<DashboardIcon />}
            onClick={irACentroCotizaciones}
            sx={{
              height: 40,
              px: '14px',
              color: '#A3489D',
              borderColor: '#A3489D',
              borderRadius: '8px',
              fontSize: 17,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { background: '#F4EAF4' },
            }}
          >
            Centro de cotizaciones
          </Button>
        </Box>
        <Typography
          data-testid="detalle-cotizacion-descripcion"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: 15,
            color: '#A79AA7',
            marginTop: '4px',
          }}
        >
          Consulta y ajusta los productos, bolsas y precios de esta cotización.
        </Typography>

        {/* SPEC-171 — entrega (izq) y validez (der) en la misma fila, top-aligned; apilan en móvil */}
        <Box
          data-testid="entrega-validez-row"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
            alignItems: 'start',
            gap: 1.5,
            mt: 1.5,
          }}
        >
          {/* SPEC-159 — sitio de entrega de la mercancía (inline, prefill del cliente, auto-guardado) */}
          <SitioEntregaBlock
            bolsaId={bolsa.id}
            entrega={bolsa.entrega ?? ENTREGA_VACIA}
            entregaSugerida={bolsa.entregaSugerida ?? null}
            soloLectura={soloLectura}
            sinMargenSuperior
          />

          {/* SPEC-167 — fecha de validez de construcción (sugerencia hoy+15, rango (hoy, hoy+15]) */}
          <ValidezBlock
            validez={validez}
            validezSugerida={validezSugerida}
            soloLectura={soloLectura}
            guardando={validezGuardando}
            error={validezError}
            onCambiar={actualizarValidez}
            sinMargenSuperior
          />
        </Box>
      </Box>

      {/* SPEC-119: franja de chips de bolsas abiertas — reemplaza el selector dropdown */}
      {/* SPEC-136: Crear PDF y Borrar bolsa (bolsa activa) se reubican aquí, junto a Gestionar bolsas */}
      <BagStrip
        bolsas={opcionesBolsa}
        bolsaActivaId={bolsa.id}
        onSeleccionarBolsa={handleSeleccionarBolsa}
        onNuevaBolsa={handleAbrirNuevaBolsa}
        onGestionar={() => setManageModalOpen(true)}
        onBorrarBolsa={handleAbrirBorrarChip}
        onPreviewPdf={handlePreviewPdf}
        onCrearPdf={crearPdf}
        crearPdfDisabled={crearPdfDisabled}
        onBorrarBolsaActiva={handleAbrirBorrarActiva}
        borrarBolsaActivaDisabled={soloLectura}
      />
      {error && (
        <Box sx={{ px: 1, pt: 1 }}>
          <Typography data-testid="operation-error" sx={{ color: '#E03030', fontSize: 16, fontWeight: 500 }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* SPEC-206 (RN-010/CA-011) — en un borrador con versiones enviadas: qué se edita y qué tiene el cliente */}
      {bolsa.estadoCodigo === EstadoBolsa.BORRADOR && bolsa.versionVigente && (
        <Box sx={{ px: 1 }}>
          <AvisoEdicionVersion
            siguienteVersion={bolsa.siguienteVersion ?? bolsa.versionVigente.version + 1}
            versionVigente={bolsa.versionVigente.version}
            enviadaEn={bolsa.versionVigente.enviadaEn}
          />
        </Box>
      )}

      {/* Tabla — ancho completo (sin panel lateral) */}
      <Box>
        <ProductTableToolbar
          productoCount={lineasFiltradas.length}
          lineasCount={bolsa.lineas.length}
          soloLectura={soloLectura}
          busqueda={busqueda}
          onBusquedaChange={setBusqueda}
          onAplicarUtilidadMasiva={aplicarUtilidadMasiva}
          onAplicarCantidadMasiva={aplicarCantidadMasiva}
        />

        {/* Wrapper exterior — scroll horizontal cuando minWidths superan el viewport */}
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          {/* Encabezado de tabla — sticky vertical dentro del scroll */}
          <Box
            data-testid="table-header"
            sx={{
              display: 'grid',
              gridTemplateColumns,
              alignItems: 'center',
              background: '#FCF3FC',
              borderBottom: '1px solid #DFDCE2',
              height: '56px',
              px: '14px',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            {columnas.map((col, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#57205F',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  textAlign: i === 0 ? 'left' : 'center',
                  // SPEC-131: índices recorridos tras reincorporar la columna Transporte
                  // (Precio de venta pasa de índice 8 a 9).
                  // SPEC-148: la separación de 16px la lleva Cantidad; Precio de venta conserva su
                  // separador. SPEC-208: por etiqueta, porque la columna "Opción" corre los índices.
                  paddingLeft: col.label === 'Cantidad' ? '16px' : col.label === 'Precio de venta' ? '10px' : undefined,
                  borderLeft: col.label === 'Precio de venta' ? '2px solid #D8CDE0' : undefined,
                }}
              >
                {col.label}
              </Typography>
            ))}
          </Box>

          {/* Filas con scroll vertical */}
          <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
            <Box data-testid="product-table">
              {/* SPEC-119 (R-09, CA-019): bolsa sin líneas — mensaje contextual en vez de tabla vacía. */}
              {bolsa.lineas.length === 0 ? (
                <Typography
                  data-testid="mensaje-bolsa-vacia"
                  sx={{ textAlign: 'center', color: '#8A8F99', fontSize: 17, py: 5 }}
                >
                  La bolsa está vacía — agrega productos del catálogo
                </Typography>
              ) : lineasFiltradas.length === 0 ? (
                /* SPEC-119 (R-09, CA-020): búsqueda sin coincidencias. */
                <Typography
                  data-testid="mensaje-busqueda-sin-resultados"
                  sx={{ textAlign: 'center', color: '#8A8F99', fontSize: 17, py: 5 }}
                >
                  Ningún producto coincide con la búsqueda
                </Typography>
              ) : (
              <>
              {/* SPEC-172 (RN-001): solo se montan las primeras `tope` filas del filtrado; el
                  resto bajo demanda vía «cargar más» + centinela. RN-003: los handlers del hook
                  se pasan DIRECTOS (sin arrow wrappers) para no romper el React.memo de la fila. */}
              {lineasVisibles.map((linea) => {
                const state = lineasState[linea.id];
                const varianteActual = getVarianteActual(linea.id, linea.coloresVariantes);
                return (
                  <ProductRowCard
                    key={linea.id}
                    gridTemplateColumns={gridTemplateColumns}
                    linea={linea}
                    cantidad={state?.cantidad ?? linea.cantidad}
                    precioVenta={state?.precioVenta ?? linea.precioVenta ?? linea.precioUnitario}
                    colorValue={state?.colorSeleccionado ?? null}
                    marcacionValue={state?.tipoMarcacionId ?? null}
                    precioMarcacion={state?.precioMarcacion ?? 0}
                    marcacionOpcionId={state?.marcacionOpcionId ?? null}
                    marcacionProveedorId={state?.marcacionProveedorId ?? null}
                    stockActual={varianteActual?.stock ?? 0}
                    precioProveedorActual={state?.precioProveedorActual ?? varianteActual?.precio ?? linea.precioUnitario}
                    descuentoProveedor={state?.descuentoProveedor ?? linea.descuentoProveedor}
                    origenDescuento={state?.origenDescuento}
                    transporte={state?.transporte ?? linea.transporte}
                    utilidad={state?.utilidad ?? 22}
                    tiposMarcacion={tiposMarcacion}
                    coloresVariantes={linea.coloresVariantes}
                    onCantidadChange={cambiarCantidad}
                    onTransporteChange={cambiarTransporte}
                    onPrecioVentaChange={cambiarPrecioVenta}
                    onColorChange={cambiarColor}
                    mostrarOpcion={conOpcion}
                    onOpcionChange={cambiarOpcion}
                    onMarcacionChange={cambiarMarcacion}
                    onMarcacionConfirm={confirmarMarcacion}
                    onUtilidadChange={cambiarUtilidad}
                    onProductoClick={handleProductoClick}
                    onDuplicar={handleDuplicar}
                    duplicando={duplicandoId === linea.id}
                    onBorrar={eliminarLinea}
                    soloLectura={soloLectura}
                  />
                );
              })}
              {/* SPEC-172 (RN-001, CA-003): centinela de scroll + botón de respaldo. Monta la
                  siguiente tanda mientras queden líneas filtradas sin montar. */}
              {hayMasLineas && (
                <Box
                  ref={sentinelRef}
                  sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
                >
                  <Button
                    variant="outlined"
                    data-testid="btn-cargar-mas-lineas"
                    onClick={cargarMas}
                    sx={{
                      borderColor: '#A3489D',
                      color: '#A3489D',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: 15,
                      '&:hover': { background: '#F4EAF4' },
                    }}
                  >
                    Cargar más ({lineasVisibles.length} de {lineasFiltradas.length})
                  </Button>
                </Box>
              )}
              </>
              )}
            </Box>
          </Box>
        </Box>{/* fin wrapper exterior */}

        {/* Footer de tabla */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            data-testid="btn-abrir-modal-agregar"
            onClick={() => setModalAgregarOpen(true)}
            disabled={soloLectura}
            sx={{
              borderStyle: 'dashed',
              borderColor: '#A3489D',
              color: '#A3489D',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 16,
              '&:hover': { background: '#F4EAF4', borderStyle: 'dashed' },
              '&.Mui-disabled': { borderColor: '#D9C6D8', color: '#C8A0D8' },
            }}
          >
            Agregar producto
          </Button>
          {/* SPEC-172 (RN-004): filas montadas visibles sobre el total del filtro. */}
          <Typography data-testid="indicador-mostrando" sx={{ fontSize: 15, color: '#8A8F99' }}>
            Mostrando {lineasVisibles.length} de {lineasFiltradas.length}
          </Typography>
        </Box>

        {/* CA-021: QuotationSummaryCard debajo de la tabla */}
        <QuotationSummaryCard
          descuento={descuento}
          onDescuentoChange={(v) => { setDescuento(v); marcarCambioPendiente(); }}
          subtotal={subtotal}
          onGuardar={guardarCotizacion}
          guardando={guardando}
          guardado={guardado}
          soloLectura={soloLectura}
          referencias={referenciasDistintas}
          unidadesTotales={unidadesTotales}
        />
      </Box>
      <ProductoDetalleModal
        open={modalLineaId !== null}
        producto={productoModal}
        onClose={() => setModalLineaId(null)}
        onAgregar={() => {}}
        selectedBolsaId={null}
        hideAgregar
      />
      {bolsa && (
        <SeleccionarProductoModal
          open={modalAgregarOpen}
          bolsaId={bolsa.id}
          onClose={() => setModalAgregarOpen(false)}
          onProductoAgregado={agregarProducto}
        />
      )}
      <NuevaBolsaModal
        open={nuevaBolsaModalOpen}
        onClose={() => setNuevaBolsaModalOpen(false)}
        onCreateBolsa={handleCrearBolsa}
      />
      <Dialog
        data-testid="dialog-confirmar-cambio-bolsa"
        open={pendingAction !== null}
        onClose={handleCancelarPendingAction}
      >
        <DialogTitle>Cambios sin guardar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tienes ediciones sin guardar en esta cotización. Si continúas, se perderán
            {pendingAction?.type === 'nuevaBolsa' ? ' al crear la bolsa nueva.' : ' al cambiar de bolsa.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="btn-cancelar-cambio-bolsa"
            onClick={handleCancelarPendingAction}
            sx={{ color: '#8A8F99', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            data-testid="btn-confirmar-cambio-bolsa"
            onClick={handleConfirmarPendingAction}
            variant="contained"
            sx={{ background: '#A3489D', textTransform: 'none', '&:hover': { background: '#8A3A85' } }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {/* SPEC-119 (R-11): gestión en lote */}
      <BagManageModal
        open={manageModalOpen}
        bolsas={opcionesBolsa}
        onClose={() => setManageModalOpen(false)}
        onSolicitarBorrado={handleSolicitarBorradoDesdeGestion}
      />

      {/* SPEC-119 (RN-007): único camino a POST /bolsas/discard, converge desde los 3 puntos de entrada */}
      {deleteTargetIds && (
        <BagDeleteConfirm
          open={deleteTargetIds !== null}
          bolsas={deleteTargetIds
            .map(id => opcionesBolsa.find(b => b.id === id))
            .filter((b): b is BolsaResumen => b !== undefined)}
          onCancel={() => setDeleteTargetIds(null)}
          onConfirmed={handleDiscardConfirmado}
        />
      )}

      {resumenDuplicado && (
        <ReprecioResumenModal
          titulo={resumenDuplicado.titulo}
          reprecio={resumenDuplicado.reprecio}
          onCerrar={() => setResumenDuplicado(null)}
        />
      )}

      <Snackbar
        open={toast !== null}
        autoHideDuration={2600}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
